import React, { useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import Home from "@mui/icons-material/Home";
import ResultItem from "../components/ResultItem";

function OptimalRentCalculator() {
  const [basicSalary, setBasicSalary] = useState("");
  const [hraProvided, setHraProvided] = useState("");
  const [cityType, setCityType] = useState("metro");
  const [result, setResult] = useState(null);

  const amount = (value) => Number(value.replace(/,/g, "")) || 0;
  const formatNumber = (value) => value ? Number(value.replace(/,/g, "")).toLocaleString("en-IN") : "";
  const handleAmountChange = (setter) => (event) => setter(event.target.value.replace(/\D/g, ""));

  const calculateOptimalRent = (event) => {
    event.preventDefault();
    const basic = amount(basicSalary);
    const limit = basic * (cityType === "metro" ? 0.5 : 0.4);
    const hra = hraProvided ? amount(hraProvided) : limit;
    const exemptHra = Math.min(hra, limit);
    const annualRent = exemptHra + basic * 0.1;

    setResult({ basic, hra, limit, annualRent, exemptHra });
  };

  return (
    <Container component="main" sx={{ flex: 1, py: 4 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          mt: 3,
          pb: 2,
          color: "primary.main",
          fontWeight: 700,
          textAlign: "center",
          textTransform: "uppercase",
          letterSpacing: 1.2,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Home sx={{ fontSize: "2.5rem", color: "primary.dark" }} />
        Optimal Rent Calculator
      </Typography>

      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
        Find the minimum annual rent required to maximise your HRA exemption.
      </Typography>

      <form onSubmit={calculateOptimalRent}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField fullWidth required label="Annual Basic Salary" value={formatNumber(basicSalary)} onChange={handleAmountChange(setBasicSalary)} helperText="Annual basic salary is the fixed basic component for the year, not total gross salary. It is commonly around 40% to 50% of gross salary, but your employer's salary structure determines the actual amount." />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Annual HRA Provided by Company" value={formatNumber(hraProvided)} onChange={handleAmountChange(setHraProvided)} helperText={`Leave blank to use ${cityType === "metro" ? "50%" : "40%"} of basic salary.`} />
          </Grid>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <Typography variant="subtitle1" gutterBottom>City of Residence</Typography>
              <RadioGroup row value={cityType} onChange={(event) => setCityType(event.target.value)}>
                <FormControlLabel value="metro" control={<Radio />} label="Metro city (50% limit)" />
                <FormControlLabel value="non-metro" control={<Radio />} label="Non-metro city (40% limit)" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" color="primary" type="submit" disabled={!basicSalary}>Calculate Optimal Rent</Button>
          </Grid>
        </Grid>
      </form>

      {result && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>Optimal Rent Details</Typography>
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Grid container spacing={2}>
                <ResultItem label="Annual Basic Salary" value={result.basic} sx={{ bgcolor: "primary.light", borderRadius: 1, p: 1 }} />
                <ResultItem label="Annual HRA Considered" value={result.hra} sx={{ bgcolor: "primary.light", borderRadius: 1, p: 1 }} />
                <ResultItem label="Maximum HRA Exemption by City" value={result.limit} sx={{ bgcolor: "primary.light", borderRadius: 1, p: 1 }} />
                <ResultItem label="Optimal Annual Rent" value={result.annualRent} boldLabel sx={{ bgcolor: "success.light", borderRadius: 1, p: 1 }} />
                <ResultItem label="Optimal Monthly Rent" value={Math.ceil(result.annualRent / 12)} boldLabel sx={{ bgcolor: "success.light", borderRadius: 1, p: 1 }} />
                <ResultItem label="Expected HRA Exemption" value={result.exemptHra} sx={{ bgcolor: "info.light", borderRadius: 1, p: 1 }} />
              </Grid>
            </CardContent>
          </Card>
        </Box>
      )}
    </Container>
  );
}

export default OptimalRentCalculator;
